import React, {
  useState,
  useCallback,
  useContext,
} from 'react';
import { IMaskInputProps } from 'react-imask';
import {
  useForm,
  useFieldArray,
  Controller,
  FieldError,
} from 'react-hook-form';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  useTheme,
  Theme,
  Button,
  Alert,
  Autocomplete,
  Chip,
  IconButton,
  FormControl,
  FormLabel,
  Select,
  Option,
  FormHelperText,
  Radio,
  RadioGroup,
} from '@mui/joy';
import { tss } from 'tss-react';
import { ControlledInput } from 'src/components/ControlledInput';
import CurrencyInput from 'src/components/CurrencyInput';
import {
  EmailOutlined, LockOutlined, Add as AddIcon, Close as CloseIcon,
  InfoOutlined,
  Delete,
} from '@mui/icons-material';
import {
  ListingBuyerType,
  Type24EnumsListingBuyerType,
} from 'src/generated/api';
import { getAPI } from 'src/api';
import { logEvent } from 'src/analytics';
import PhoneMaskAdapter, { PhoneMaskAdapterCustomProps } from 'src/components/PhoneMask';
import { ListingAuthContext } from '../ListingAuthContext';
import { loggingEvents } from '../loggingEvents';

interface ContactModalProps {
  onClose: () => void;
}

interface EmailFormData {
  email: string;
}

interface OtpFormData {
  otp: string;
}

interface UserInfoFormData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  type: string;
  linkedinUrl: string;
  firmName: string;
  jobTitle: string;
  criteriaEbitdaRange: string;
  criteriaDealSizeMin: number;
  criteriaDealSizeMax: number;
  criteriaIndustryFocus: string[];
  criteriaRevenueRange: string;
  previousAcquisitions: { value: string }[];
  hasIndustryFocus: boolean | null;
  hasCompletedAcquisitions: boolean | null;
  criteriaIndustryFocusInput: string;
}

const listingBuyerTypeOptions = [
  { key: Type24EnumsListingBuyerType.PRIVATE_EQUITY, label: 'Private Equity (PE) Firm' },
  { key: Type24EnumsListingBuyerType.STRATEGIC, label: 'Corporate / Strategic Buyer' },
  { key: Type24EnumsListingBuyerType.SEARCH_FUND, label: 'Independent Sponsor / Search Fund' },
  { key: Type24EnumsListingBuyerType.INDIVIDUAL, label: 'Individual Investor' },
];

const PhoneMaskAdapterRef = React.forwardRef<
HTMLInputElement,
PhoneMaskAdapterCustomProps & IMaskInputProps<HTMLInputElement>
>(
  PhoneMaskAdapter,
);

const useStyles = tss
  .withParams<{ theme: Theme }>()
  .create(({ theme }) => ({
    modal: {
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'scroll',
      gap: 0,
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      marginTop: theme.spacing(1),
      gap: theme.spacing(3),
      overflowY: 'auto',
      width: 700,
    },
    thinForm: {
      display: 'flex',
      flexDirection: 'column',
      marginTop: theme.spacing(2),
      gap: theme.spacing(2),
      width: 400,
    },
    row: {
      display: 'flex',
      flexDirection: 'row',
      gap: theme.spacing(2),
      width: '100%',
    },
    group: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
    fullWidth: {
      width: '100%',
    },
    previousAcquisition: {
      display: 'flex',
      flexDirection: 'row',
      gap: theme.spacing(2),
      alignItems: 'flex-end',
      width: '100%',
    },
  }));

type Step = 'email' | 'otp' | 'userInfo' | 'criteriaInfo';

export const ebitdaRangeOptions = [
  { value: '< 500k', label: 'Less than $500,000' },
  { value: '500k - 1m', label: '$500,000 - $1 million' },
  { value: '1m - 5m', label: '$1 million - $5 million' },
  { value: '> 5m', label: 'More than $5 million' },
];

export const revenueRangeOptions = [
  { value: '< 1m', label: 'Less than $1 million' },
  { value: '1m - 5m', label: '$1 million - $5 million' },
  { value: '5m - 20m', label: '$5 million - $20 million' },
  { value: '> 20m', label: 'More than $20 million' },
];

export function LogInModal({
  onClose,
}: ContactModalProps) {
  const theme = useTheme();
  const { refetch, user } = useContext(ListingAuthContext);
  const { classes } = useStyles({ theme });
  const [step, setStep] = useState<Step>('email');
  const [newAccount, setNewAccount] = useState(false);
  const [email, setEmail] = useState('');

  const emailForm = useForm<EmailFormData>();
  const otpForm = useForm<OtpFormData>();
  const userInfoForm = useForm<UserInfoFormData>({
    mode: 'onSubmit',
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phoneNumber: user?.phoneNumber || '',
      type: user?.type || '',
      linkedinUrl: user?.linkedinUrl || '',
      firmName: user?.firmName || '',
      jobTitle: user?.jobTitle || '',
      hasIndustryFocus: null,
      hasCompletedAcquisitions: null,
      criteriaIndustryFocus: [],
      criteriaIndustryFocusInput: '',
      previousAcquisitions: [],
    },
  });

  const api = getAPI();

  const handleEmailSubmit = useCallback(async (data: EmailFormData) => {
    logEvent({
      name: loggingEvents.auth.submitEmail,
      type: 'response',
    });
    const res = await api.listings.sendOneTimeCode({ email: data.email });
    setEmail(data.email);
    setNewAccount(res.data.newAccount);
    setStep('otp');
  }, [api]);

  const handleOtpSubmit = useCallback(async (data: OtpFormData) => {
    logEvent({
      name: loggingEvents.auth.submitOtp,
      type: 'response',
    });
    try {
      await api.listings.verifyOneTimeCode({ email, code: data.otp });
      await refetch();
      if (newAccount) {
        setStep('userInfo');
      } else {
        onClose();
      }
    } catch (err) {
      otpForm.setError('otp', { message: 'Invalid one-time code' });
    }
  }, [api, email, otpForm, refetch, onClose, newAccount]);

  const {
    handleSubmit,
    formState: { errors },
    control,
  } = userInfoForm;

  const handleUserInfoSubmit = useCallback(async (data: UserInfoFormData) => {
    logEvent({
      name: loggingEvents.auth.submitUserInfo,
      type: 'response',
    });
    await api.listings.updateProfile({
      firstName: data.firstName,
      lastName: data.lastName,
      phoneNumber: data.phoneNumber,
      type: data.type as ListingBuyerType,
      criteriaEbitdaRange: data.criteriaEbitdaRange,
      criteriaDealSizeMin: data.criteriaDealSizeMin,
      criteriaDealSizeMax: data.criteriaDealSizeMax,
      criteriaRevenueRange: data.criteriaRevenueRange,
      linkedinUrl: data.linkedinUrl,
      firmName: data.firmName,
      jobTitle: data.jobTitle,
      criteriaIndustryFocus: data.hasIndustryFocus ? data.criteriaIndustryFocus : [],
      previousAcquisitions: data.hasCompletedAcquisitions ?
        data.previousAcquisitions.map((value) => value.value.trim()) : [],
    });
    await refetch();
    onClose();
  }, [api, refetch, onClose]);

  const {
    fields: previousAcquisitionsFields,
    append: appendPreviousAcquisition,
    remove: removePreviousAcquisition,
  } = useFieldArray({
    control: userInfoForm.control,
    name: 'previousAcquisitions',
  });

  const showFirmField = userInfoForm.watch('type') === Type24EnumsListingBuyerType.PRIVATE_EQUITY ||
    userInfoForm.watch('type') === Type24EnumsListingBuyerType.STRATEGIC;

  const onUserInfoNext = handleSubmit(() => {
    if (Object.keys(errors).length === 0) {
      setStep('criteriaInfo');
    }
  });

  return (
    <Modal open onClose={onClose}>
      <ModalDialog className={classes.modal}>
        <ModalClose />
        <>
          {step === 'email' && (
            <form
              onSubmit={emailForm.handleSubmit(handleEmailSubmit)}
              className={classes.thinForm}
            >
              <Typography level='h4'>Log In / Sign Up</Typography>
              <Typography level='body-md'>
                Log in or register for a free OffDeal buyer account to submit
                NDAs and access data rooms for deals.
              </Typography>
              <ControlledInput
                label="Email Address"
                name="email"
                inputProps={{
                  placeholder: 'Enter your work email',
                  onFocus: () => logEvent({
                    name: loggingEvents.auth.focusEmailInput,
                    type: 'click',
                  }),
                }}
                control={emailForm.control}
                required
                StartDecorator={EmailOutlined}
              />
              <Button type="submit">Next</Button>
            </form>
          )}
          {step === 'otp' && (
            <form
              onSubmit={otpForm.handleSubmit(handleOtpSubmit)}
              className={classes.thinForm}
            >
              <Typography level='h4'>Check Your Email</Typography>
              {
                otpForm.formState.errors.otp && (
                  <Alert color="danger">
                    {otpForm.formState.errors.otp.message}
                  </Alert>
                )
              }
              <Typography level='body-md'>
                Enter the 6-digit code sent to your email.
                This helps us ensure the security of sensitive information.
              </Typography>
              <ControlledInput
                label="One-Time Code"
                name="otp"
                control={otpForm.control}
                required
                StartDecorator={LockOutlined}
                inputProps={{
                  placeholder: 'Enter the 6-digit code',
                  inputMode: 'numeric',
                  onFocus: () => logEvent({
                    name: loggingEvents.auth.focusOtpInput,
                    type: 'click',
                  }),
                }}
                pattern="[0-9]{6}"
              />
              <Button type="submit">Submit Code</Button>
            </form>
          )}
          {step === 'userInfo' && (
            <>
              <form onSubmit={onUserInfoNext} className={classes.form}>
                <div>
                  <Typography level='h4'>Complete Your Profile</Typography>
                  <Typography level='body-md'>
                    Please provide some basic information about yourself.
                    This helps us tailor opportunities to your interests
                  </Typography>
                </div>
                <div className={classes.row}>
                  <ControlledInput
                    label="First Name"
                    name="firstName"
                    control={userInfoForm.control}
                    className={classes.fullWidth}
                    required
                    error={errors.firstName}
                  />
                  <ControlledInput
                    label="Last Name"
                    name="lastName"
                    control={userInfoForm.control}
                    className={classes.fullWidth}
                    required
                    error={errors.lastName}
                  />
                </div>
                <div className={classes.row}>
                  <ControlledInput
                    label="Phone Number"
                    name="phoneNumber"
                    required
                    control={userInfoForm.control}
                    className={classes.fullWidth}
                    error={errors.phoneNumber}
                    inputProps={{
                      slotProps: {
                        input: {
                          component: PhoneMaskAdapterRef,
                        },
                      },
                    }}
                  />
                  <ControlledInput
                    label="LinkedIn URL (Optional)"
                    name="linkedinUrl"
                    control={userInfoForm.control}
                    className={classes.fullWidth}
                    error={errors.linkedinUrl}
                  />
                </div>
                <Controller
                  name="type"
                  control={control}
                  rules={{ required: 'This field is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <FormControl error={!!error}>
                      <FormLabel>Investor Type</FormLabel>
                      <Select
                        {...field}
                        size="lg"
                        value={field.value || ''}
                        onChange={(_, newValue) => field.onChange(newValue)}
                      >
                        {listingBuyerTypeOptions.map((option) => (
                          <Option key={option.key} value={option.key}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                      {error && (
                        <FormHelperText>
                          <InfoOutlined />
                          {error.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
                {showFirmField && (
                  <div className={classes.row}>
                    <ControlledInput
                      label="Firm Name"
                      name="firmName"
                      control={userInfoForm.control}
                      required={showFirmField}
                      className={classes.fullWidth}
                      error={errors.firmName}
                    />
                    <ControlledInput
                      label="Job Title"
                      name="jobTitle"
                      control={userInfoForm.control}
                      required={showFirmField}
                      className={classes.fullWidth}
                      error={errors.jobTitle}
                    />
                  </div>
                )}
                <Button type="submit">
                  Next
                </Button>
              </form>
            </>
          )}
          {step === 'criteriaInfo' && (
            <>
              <Typography level='h4'>Investment Criteria</Typography>
              <Typography level='body-md'>
                Help us understand what types of businesses you're interested in acquiring.
              </Typography>
              <form
                onSubmit={userInfoForm.handleSubmit(handleUserInfoSubmit)}
                className={classes.form}
              >
                <FormControl error={!!errors.criteriaRevenueRange}>
                  <FormLabel>Target Revenue Range</FormLabel>
                  <Controller
                    name="criteriaRevenueRange"
                    control={control}
                    rules={{ required: 'This is required' }}
                    render={({ field }) => (
                      <RadioGroup {...field}>
                        {revenueRangeOptions.map((option) => (
                          <Radio key={option.value} value={option.value} label={option.label} />
                        ))}
                      </RadioGroup>
                    )}
                  />
                  {errors.criteriaRevenueRange && (
                    <FormHelperText>
                      <InfoOutlined />
                      {(errors.criteriaRevenueRange as FieldError).message}
                    </FormHelperText>
                  )}
                </FormControl>
                <FormControl error={!!errors.criteriaEbitdaRange}>
                  <FormLabel>Target EBITDA Range</FormLabel>
                  <Controller
                    name="criteriaEbitdaRange"
                    control={control}
                    rules={{ required: 'This field is required' }}
                    render={({ field }) => (
                      <RadioGroup {...field}>
                        {ebitdaRangeOptions.map((option) => (
                          <Radio key={option.value} value={option.value} label={option.label} />
                        ))}
                      </RadioGroup>
                    )}
                  />
                  {errors.criteriaEbitdaRange && (
                    <FormHelperText>
                      <InfoOutlined />
                      {(errors.criteriaEbitdaRange as FieldError).message}
                    </FormHelperText>
                  )}
                </FormControl>
                <div className={classes.group}>
                  <Typography level='body-md'>
                    <b>
                      Preferred Deal Size
                    </b>
                  </Typography>
                  <div className={classes.row}>
                    <CurrencyInput
                      label="Minimum ($)"
                      name="criteriaDealSizeMin"
                      control={userInfoForm.control}
                      required
                      inputProps={{
                        inputMode: 'numeric',
                      }}
                    />
                    <CurrencyInput
                      label="Maximum ($)"
                      name="criteriaDealSizeMax"
                      control={userInfoForm.control}
                      required
                      inputProps={{
                        inputMode: 'numeric',
                      }}
                    />
                  </div>
                </div>
                <div className={classes.group}>
                  <Typography level='body-md'>
                    <b>Do you have an industry focus?</b>
                  </Typography>
                  <FormControl error={!!errors.hasIndustryFocus}>
                    <Controller
                      name="hasIndustryFocus"
                      control={control}
                      rules={{
                        validate: (value) => value !== null || 'This is required',
                      }}
                      render={({ field }) => (
                        <RadioGroup
                          orientation="horizontal"
                          value={field.value === null ? '' : field.value ? 'yes' : 'no'}
                          onChange={(event) => {
                            const newValue = event.target.value === 'yes';
                            field.onChange(newValue);
                          }}
                        >
                          <Radio value="yes" label="Yes" />
                          <Radio value="no" label="No" />
                        </RadioGroup>
                      )}
                    />
                    {errors.hasIndustryFocus && (
                      <FormHelperText>
                        <InfoOutlined />
                        {errors.hasIndustryFocus.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                  {userInfoForm.watch('hasIndustryFocus') && (
                    <FormControl error={!!errors.criteriaIndustryFocus}>
                      <FormLabel>
                        Please specify your industry focus (e.g. “SaaS/Software”,
                        “facilities management”, “residential services”)
                      </FormLabel>
                      <Controller
                        name="criteriaIndustryFocus"
                        control={control}
                        rules={{ required: 'Please enter at least one industry focus' }}
                        render={({ field }) => (
                          <Autocomplete
                            multiple
                            freeSolo
                            options={[]}
                            value={field.value}
                            onChange={(_, newValue) => field.onChange(newValue)}
                            inputValue={userInfoForm.watch('criteriaIndustryFocusInput') || ''}
                            onInputChange={(_, newInputValue) => {
                              userInfoForm.setValue('criteriaIndustryFocusInput', newInputValue);
                            }}
                            onBlur={() => {
                              const inputValue = userInfoForm.watch('criteriaIndustryFocusInput').trim();
                              if (inputValue && !field.value.includes(inputValue)) {
                                field.onChange([...field.value, inputValue]);
                                userInfoForm.setValue('criteriaIndustryFocusInput', '');
                              }
                            }}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip
                                  variant="soft"
                                  color="warning"
                                  {...getTagProps({ index })}
                                  key={option}
                                  endDecorator={
                                    <IconButton
                                      size="sm"
                                      variant="plain"
                                      color="neutral"
                                      onClick={() => {
                                        const newValue = field.value
                                          .filter((_, i) => i !== index);
                                        field.onChange(newValue);
                                      }}
                                    >
                                      <CloseIcon />
                                    </IconButton>
                                  }
                                >
                                  {option}
                                </Chip>
                              ))
                            }
                          />
                        )}
                      />
                      {errors.criteriaIndustryFocus && (
                        <FormHelperText>
                          <InfoOutlined />
                          {errors.criteriaIndustryFocus.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                </div>
                <div className={classes.group}>
                  <Typography level='body-md'>
                    <b>Have you completed acquisitions in the past?</b>
                  </Typography>
                  <FormControl error={!!errors.hasCompletedAcquisitions}>
                    <Controller
                      name="hasCompletedAcquisitions"
                      control={control}
                      rules={{
                        validate: (value) => value !== null || 'This is required',
                      }}
                      render={({ field }) => (
                        <RadioGroup
                          orientation="horizontal"
                          value={field.value === null ? '' : field.value ? 'yes' : 'no'}
                          onChange={(event) => {
                            const newValue = event.target.value === 'yes';
                            field.onChange(newValue);
                          }}
                        >
                          <Radio value="yes" label="Yes" />
                          <Radio value="no" label="No" />
                        </RadioGroup>
                      )}
                    />
                    {errors.hasCompletedAcquisitions && (
                      <FormHelperText>
                        <InfoOutlined />
                        {errors.hasCompletedAcquisitions.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                  {userInfoForm.watch('hasCompletedAcquisitions') && (
                    <>
                      <Typography level='body-md'>
                        Please list recent acquisitions or provide examples
                        (i.e. company name, sector, year acquired):
                      </Typography>
                      {previousAcquisitionsFields.length === 0 && (
                        <div key="default" style={{ display: 'flex', gap: '8px' }}>
                          <ControlledInput
                            label="Previous Acquisition 1"
                            name="previousAcquisitions.0.value"
                            control={userInfoForm.control}
                            className={classes.fullWidth}
                            required
                          />
                        </div>
                      )}
                      {previousAcquisitionsFields.map((field, index) => (
                        <div key={field.id} className={classes.previousAcquisition}>
                          <ControlledInput
                            label={`Previous Acquisition ${index + 1}`}
                            name={`previousAcquisitions.${index}.value`}
                            control={userInfoForm.control}
                            className={classes.fullWidth}
                            required
                          />
                          {previousAcquisitionsFields.length > 1 && (
                            <IconButton
                              size="lg"
                              variant="outlined"
                              color="danger"
                              onClick={() => removePreviousAcquisition(index)}
                            >
                              <Delete />
                            </IconButton>
                          )}
                        </div>
                      ))}
                      <Button
                        startDecorator={<AddIcon />}
                        onClick={() => appendPreviousAcquisition({ value: '' })}
                        variant="outlined"
                        color="success"
                      >
                        Add Previous Acquisition
                      </Button>
                    </>
                  )}
                </div>
                <Button type="submit">Submit</Button>
              </form>
            </>
          )}
        </>
      </ModalDialog>
    </Modal>
  );
}
